// Pipeline stage strip rendered inside the processing console.
import React from "react";
import { AlertOctagon } from "lucide-react";
import {
  PIPELINE_STAGES,
  type ConsoleStageState,
  type PipelineRun,
} from "./processingPipeline";

export function ConsolePipelineStrip({
  run,
  stageState,
  apiStageStates,
  failurePop,
}: {
  run: PipelineRun;
  stageState: (i: number) => ConsoleStageState;
  apiStageStates: ConsoleStageState[] | null;
  failurePop: { index: number; label: string; visible: boolean } | null;
}) {
  const stages = PIPELINE_STAGES;
  return (
    <div className="relative flex items-start gap-0 overflow-x-auto overflow-y-visible px-4 pb-2.5 pt-3">
      {stages.map((s, i) => {
        const state = stageState(i);
        const Icon = s.icon;
        return (
          <React.Fragment key={s.key}>
            <div className="relative flex w-[68px] flex-none flex-col items-center gap-1.5">
              {failurePop && failurePop.index === i && (
                <div
                  role="status"
                  aria-live="polite"
                  className="pointer-events-none absolute left-1/2 top-full z-30 mt-3 whitespace-nowrap"
                  style={{
                    animation: failurePop.visible
                      ? "rgc-fail-pop .32s cubic-bezier(.2,.9,.25,1.2) both"
                      : "rgc-fail-out .35s ease-in forwards",
                  }}
                >
                  <span className="absolute -top-1 left-1/2 size-2 -translate-x-1/2 rotate-45 rounded-[2px] border-l border-t border-red-300 bg-red-500 dark:border-red-500/50" />
                  <div className="flex items-center gap-1.5 rounded-lg border border-red-300 bg-red-500 px-2.5 py-1 text-[11px] font-bold text-white shadow-lg shadow-red-500/30 dark:border-red-500/50">
                    <AlertOctagon className="size-3.5 flex-none" />
                    Failed at {failurePop.label}
                  </div>
                </div>
              )}
              <div
                className={`relative grid size-9 place-items-center rounded-lg border transition-all duration-500 ${
                  state === "active"
                    ? "border-indigo-300 bg-indigo-50 text-indigo-600 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-400"
                    : state === "done"
                      ? "border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400"
                      : state === "failed"
                        ? "border-red-300 bg-red-50 text-red-600 shadow-[0_0_0_3px_rgba(239,68,68,0.12)] dark:border-red-500/50 dark:bg-red-500/10 dark:text-red-400"
                        : "border-border bg-[#f8fafc] text-[#94a3b8] dark:bg-white/[0.02] dark:text-white/30"
                }`}
                style={{
                  transform: state === "active" ? "scale(1.08)" : "none",
                  animation:
                    state === "failed"
                      ? "rgc-fail-shake 3.6s ease-in-out infinite"
                      : undefined,
                }}
              >
                <Icon className="size-[17px]" />
                {state === "active" && (
                  <span
                    className="pointer-events-none absolute -inset-1 rounded-[14px] border-[1.5px] border-indigo-400/70"
                    style={{ animation: "rgc-ring 1.5s ease-out infinite" }}
                  />
                )}
                {state === "failed" && (
                  <>
                    <span
                      className="pointer-events-none absolute -inset-1 rounded-[14px] border-[1.5px] border-red-400/70"
                      style={{ animation: "rgc-ring 1.8s ease-out infinite" }}
                    />
                    <span
                      className="pointer-events-none absolute -inset-1 rounded-[14px] border-[1.5px] border-red-400/50"
                      style={{
                        animation: "rgc-ring 1.8s ease-out infinite",
                        animationDelay: ".9s",
                      }}
                    />
                  </>
                )}
              </div>
              <div
                className={`text-center text-[10px] leading-tight ${
                  state === "pending"
                    ? "text-muted-foreground"
                    : "text-foreground"
                }`}
              >
                {s.label}
              </div>
            </div>
            {i < stages.length - 1 &&
              (state === "failed" ? (
                <div className="relative mt-[18px] flex h-0.5 min-w-[8px] flex-1 items-center gap-1">
                  <span className="h-0.5 flex-1 rounded bg-red-500" />
                  <span
                    className="size-1.5 flex-none rounded-full bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.85)]"
                    style={{
                      animation:
                        "rgc-broken-jitter 1.4s ease-in-out infinite",
                    }}
                  />
                  <span className="h-0 flex-1 rounded border-t border-dashed border-red-300 dark:border-red-500/50" />
                </div>
              ) : (
                <div className="relative mt-[18px] h-0.5 min-w-[8px] flex-1 rounded bg-[#e2e8f0] dark:bg-white/[0.08]">
                  <span
                    className="absolute inset-y-0 left-0 rounded bg-emerald-500 transition-[width] duration-500"
                    style={{
                      width: apiStageStates
                        ? state === "done"
                          ? "100%"
                          : "0%"
                        : run.done || i < run.stage
                          ? "100%"
                          : "0%",
                    }}
                  />
                </div>
              ))}
          </React.Fragment>
        );
      })}
    </div>
  );
}
